import { fetchLocations } from 'src/models/location/location';
import api from 'src/utils/ajax';
import { populateRelationshipFromRequest } from 'src/utils/populateRelationship';

import { Terminal } from './_types';
import { denormalizeTerminal, normalizeTerminal } from './normalizers';

interface GetTerminalsOptions {
  page?: number;
  perPage?: number;
}

const populateLocation = async (terminals: Terminal[]): Promise<Terminal[]> => {
  const locationsFunc = async (locationIds: string[]) => {
    const { locations } = await fetchLocations({
      fetchAll: true,
    });
    // Convert locationIds to numbers for comparison since location.id is a number
    const numericLocationIds = locationIds.map((id) => parseInt(id, 10));
    return locations.filter((location) => {
      // Handle both string and number IDs
      const locationId =
        typeof location.id === 'string'
          ? parseInt(location.id, 10)
          : location.id;
      return numericLocationIds.includes(locationId);
    });
  };

  return populateRelationshipFromRequest({
    list: terminals,
    idKey: 'locationId',
    newKey: 'location',
    func: locationsFunc,
  });
};

export const getTerminals = async ({
  page,
  perPage,
}: GetTerminalsOptions = {}): Promise<Terminal[]> => {
  const params = {
    page: page || 1,
    per: perPage || 10,
  };

  const response = await api.get('/api/v4/fbm/card_readers', { params });
  let terminals = response.data.map((terminal: any) =>
    normalizeTerminal(terminal)
  );

  // Populate location data
  terminals = await populateLocation(terminals);

  return terminals;
};

export const getTerminal = async (id: string): Promise<Terminal> => {
  const response = await api.get(`/api/v4/fbm/card_readers/${id}`);
  return normalizeTerminal(response.data);
};

export const createTerminal = async (
  terminal: Partial<Terminal>
): Promise<Terminal> => {
  const data = denormalizeTerminal(terminal);
  const response = await api.post('/api/v4/fbm/card_readers', {
    card_reader: data,
  });
  return normalizeTerminal(response.data);
};

export const updateTerminal = async (
  terminal: Partial<Terminal>
): Promise<Terminal> => {
  const data = denormalizeTerminal(terminal);
  const response = await api.put(`/api/v4/fbm/card_readers/${terminal.id}`, {
    card_reader: data,
  });
  return normalizeTerminal(response.data);
};

export const deleteTerminal = async (id: string): Promise<void> => {
  await api.delete(`/api/v4/fbm/card_readers/${id}`);
};

export const cancelTerminalAction = async (
  terminalId: string
): Promise<any> => {
  const response = await api.post('/api/v4/carts/cancel_terminal_action', {
    card_reader_id: terminalId,
  });
  return response.data;
};
